<template>
    <!-- The Forge's Daily Digest (GET /api/ai/daily-digest, services/dailydigest.ts): made on the person's first visit after
         their local midnight and cached for the day. Shown as one closed bar with the day's state (overdue, due today, no
         due date, next meeting); the full digest opens in a popup (Keelan, 2026-10-19: "needs to stay closed with some
         states like urgent 15 overdue, you can open into a pop up window"). Setting a date goes through each item's own
         route (board card, checklist, meeting item). -->
    <section v-if="!hidden" class="fdd" :class="{ 'fdd-button-mode': buttonMode }" aria-labelledby="fdd-title">
        <button v-if="!buttonMode" type="button" class="fdd-bar" :aria-expanded="open" aria-haspopup="dialog" @click="openPop()">
            <span class="fdd-icon"><svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg></span>
            <span class="fdd-titles">
                <span id="fdd-title" class="fdd-title">Daily Digest</span>
                <span class="fdd-headline">{{ loading ? 'The Forge is putting your day together…' : digest ? digest.headline : error ? 'Not available right now' : '' }}</span>
            </span>
            <span v-if="digest" class="fdd-states" aria-label="Today at a glance">
                <span :class="{ 'fdd-chip-hot': counts.overdue }">{{ counts.overdue || 0 }} overdue</span>
                <span v-if="counts.dueToday">{{ counts.dueToday }} due today</span>
                <span v-if="counts.noDueDate" class="fdd-chip-warn">{{ counts.noDueDate }} no due date</span>
                <!-- Rocks and milestones (Keelan, 21 Oct): the most pressing milestone state, and the rocks they own -->
                <span v-if="milestoneChip" :class="milestoneChip.cls">{{ milestoneChip.text }}</span>
                <span v-if="rockCounts.rocks" :class="{ 'fdd-chip-warn': rockCounts.off }">{{ rockChip }}</span>
                <span v-if="signCount" class="fdd-chip-sign">{{ signCount }} to sign</span>
                <!-- Screen recordings handed to this person to build (Keelan, 22 Oct) -->
                <span v-if="handoffCount" class="fdd-chip-rec">{{ handoffChip }}</span>
                <span v-if="nextMeeting" class="fdd-chip-next">{{ nextMeeting }}</span>
            </span>
            <span class="fdd-open-label">Open</span>
        </button>

        <!-- The popup: the full digest -->
        <div v-if="open" class="error-msg-wrap fdd-wrap" @click.self="closePop()">
            <div class="form-wrapper slide-up fdd-pop" role="dialog" aria-modal="true" aria-label="Daily Digest" tabindex="-1" ref="pop" @keydown.esc.prevent="closePop()">
                <button type="button" class="fdd-close" title="Close" @click="closePop()">×</button>
                <div class="fdd-pop-h">
                    <span class="fdd-icon"><svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg></span>
                    <div class="fdd-titles"><span class="fdd-title">Daily Digest</span><span class="fdd-headline">{{ loading ? 'The Forge is putting your day together…' : digest ? digest.headline : '' }}</span></div>
                </div>
                <div class="fdd-body">
                    <div v-if="loading" class="fdd-loading" role="status"><div class="fdd-orb"></div>Reading your meetings, decks and to-dos…</div>
                    <div v-else-if="error" class="fdd-error" role="alert">{{ error }} <a href="#" @click.prevent="load()">Try again</a></div>
                    <template v-else-if="digest">
                        <p v-if="digest.summary" class="fdd-summary">{{ digest.summary }}</p>
                        <div class="fdd-chips">
                            <span :class="{ 'fdd-chip-hot': counts.overdue }">{{ counts.overdue || 0 }} overdue</span>
                            <span>{{ counts.dueToday || 0 }} due today</span>
                            <span>{{ counts.dueThisWeek || 0 }} due this week</span>
                            <span :class="{ 'fdd-chip-warn': counts.noDueDate }">{{ counts.noDueDate || 0 }} with no due date</span>
                            <span v-if="milestoneChip" :class="milestoneChip.cls">{{ milestoneChip.text }}</span>
                            <span v-if="rockCounts.rocks" :class="{ 'fdd-chip-warn': rockCounts.off }">{{ rockChip }}</span>
                            <span v-if="signCount" class="fdd-chip-sign">{{ signCount }} to sign</span>
                            <span v-if="handoffCount" class="fdd-chip-rec">{{ handoffChip }}</span>
                        </div>

                        <!-- Deck board only (Keelan, 20 Oct): the Forge's board actions live here instead of a third "The Forge" -->
                        <div v-if="boardActions" class="fdd-sec fdd-deck">
                            <div class="fdd-label">For this deck · the Forge</div>
                            <div class="fdd-deck-acts">
                                <button type="button" class="ai-btn ai-btn-ghost fdd-small" @click="forge('tidyboard')"><b>Tidy my board</b><span>Overdue, stale, undated cards</span></button>
                                <button type="button" class="ai-btn ai-btn-ghost fdd-small" @click="forge('planweek')"><b>Plan my week</b><span>What to focus on</span></button>
                                <button type="button" class="ai-btn ai-btn-ghost fdd-small" @click="forge('sortinbox')"><b>Sort the Inbox</b><span>Titles, columns, dates</span></button>
                            </div>
                        </div>

                        <div v-if="meetings.length" class="fdd-sec">
                            <div class="fdd-label">Meetings</div>
                            <a v-for="m in meetings" :key="m.meetingID + m.when" href="#" class="fdd-row" @click.prevent="openMeeting(m)">
                                <span class="fdd-when">{{ m.when == 'today' ? 'Today' : 'Tomorrow' }}<template v-if="m.time"> {{ timeLabel(m.time) }}</template></span>
                                <span class="fdd-main"><b>{{ m.name }}</b><span v-if="m.note">{{ m.note }}</span><span v-else-if="m.team">{{ m.team }}</span></span>
                            </a>
                        </div>

                        <div v-if="digest.urgent.length" class="fdd-sec">
                            <div class="fdd-label">Most urgent</div>
                            <a v-for="u in digest.urgent" :key="u.ref" href="#" class="fdd-row" @click.prevent="openItem(u)">
                                <span class="fdd-when" :class="{ 'fdd-hot': u.bucket == 'overdue' }">{{ u.bucket == 'overdue' ? 'Overdue' : u.bucket == 'today' ? 'Today' : shortDate(u.due) }}</span>
                                <span class="fdd-main"><b>{{ u.name }}</b><span>{{ u.where }} · {{ u.why }}</span></span>
                            </a>
                        </div>

                        <!-- Screen recordings someone sent this person to build (API pendingHandoffs, live): each opens its builder page,
                             where the Forge builds the process from it or the recording is discarded -->
                        <div v-if="handoffCount" class="fdd-sec fdd-rec">
                            <div class="fdd-label">Recordings sent to you · {{ handoffCount }}</div>
                            <a v-for="h in handoffs.items" :key="'h' + h.jobID" href="#" class="fdd-row" @click.prevent="openLink(h.link)">
                                <span class="fdd-when">{{ h.saving ? 'Saving…' : 'To build' }}</span>
                                <span class="fdd-main"><b>Screen recording from {{ h.from }}</b><span>{{ h.note || 'No note' }}<template v-if="h.created"> · sent {{ shortDate(String(h.created).slice(0, 10)) }}</template></span></span>
                            </a>
                            <div v-if="handoffCount > handoffs.items.length" class="fdd-hint">{{ handoffCount - handoffs.items.length }} more under <a href="#" @click.prevent="openLink('/ai-hub#captures')">My captures</a>.</div>
                        </div>

                        <!-- Their rocks this quarter and the milestones that are theirs (API gatherRocks, read live on every digest read).
                             Done goes through the Rocks page's own route, so its permission rules decide. -->
                        <div v-if="rocks.items.length || rocks.milestones.length" class="fdd-sec fdd-rocks">
                            <div class="fdd-label fdd-label-row">
                                <span>Rocks · {{ rocks.quarter.label }}</span>
                                <span class="fdd-label-soft">Week {{ rocks.quarter.week }} of {{ rocks.quarter.weeks }} · {{ rocks.quarter.daysLeft }} days left</span>
                            </div>
                            <a v-for="r in rocks.items" :key="'r' + r.rockID" href="#" class="fdd-row" @click.prevent="openLink(r.link)">
                                <span class="fdd-when"><span class="fdd-pill" :class="r.status == 'off' ? 'fdd-pill-bad' : 'fdd-pill-ok'">{{ r.status == 'off' ? 'Off track' : 'On track' }}</span></span>
                                <span class="fdd-main">
                                    <b>{{ r.name }}</b>
                                    <span>{{ rockLine(r) }}</span>
                                </span>
                            </a>
                            <template v-if="rocks.milestones.length">
                                <div class="fdd-sublabel">My milestones · {{ rocks.milestones.length }}</div>
                                <div v-for="m in rocks.milestones" :key="m.ref" class="fdd-due">
                                    <span class="fdd-when" :class="{ 'fdd-hot': m.bucket == 'overdue' }">{{ m.bucket == 'overdue' ? 'Overdue' : m.bucket == 'today' ? 'Today' : shortDate(m.due) }}</span>
                                    <div class="fdd-main">
                                        <a href="#" @click.prevent="openLink(m.link)"><b>{{ m.name }}</b></a>
                                        <span>{{ m.where }}<template v-if="m.why"> · {{ m.why }}</template></span>
                                        <span v-if="m.error" class="fdd-item-error" role="alert">{{ m.error }}</span>
                                    </div>
                                    <div class="fdd-due-act">
                                        <button type="button" class="ai-btn ai-btn-ghost fdd-small" :disabled="busy || m.saving" @click="milestoneDone(m)">{{ m.saving ? 'Saving…' : 'Done' }}</button>
                                    </div>
                                </div>
                            </template>
                            <div v-if="rocks.more || rockCounts.noMilestones" class="fdd-hint">
                                <template v-if="rocks.more">{{ rocks.more }} more on <a href="#" @click.prevent="openLink(myRocksLink)">My rocks</a>.</template>
                                <template v-if="rockCounts.noMilestones"> {{ rockCounts.noMilestones }} of your rocks {{ rockCounts.noMilestones == 1 ? 'has' : 'have' }} no milestones yet: on <a href="#" @click.prevent="openLink(myRocksLink)">the Rocks page</a> the Forge can draft them.</template>
                            </div>
                        </div>

                        <div v-if="digest.noDueDate.length" class="fdd-sec">
                            <div class="fdd-label fdd-label-row">
                                <span>No due date · the Forge suggests one</span>
                                <button v-if="acceptable.length > 1" type="button" class="ai-btn ai-btn-ghost fdd-small" :disabled="busy" @click="acceptAll()">Accept all {{ acceptable.length }}</button>
                            </div>
                            <div v-for="x in digest.noDueDate" :key="x.ref" class="fdd-due">
                                <div class="fdd-main">
                                    <a href="#" @click.prevent="openItem(x)"><b>{{ x.name }}</b></a>
                                    <span>{{ x.kind == 'step' ? 'Checklist step on ' : '' }}{{ x.where }}<template v-if="x.reason"> · {{ x.reason }}</template></span>
                                    <span v-if="x.error" class="fdd-item-error" role="alert">{{ x.error }}</span>
                                </div>
                                <div class="fdd-due-act">
                                    <input type="date" v-model="x.pick" :min="digest.date" :aria-label="'Due date for ' + x.name" />
                                    <button type="button" class="ai-btn fdd-small" :disabled="busy || !x.pick || x.saving" @click="accept(x)">{{ x.saving ? 'Saving…' : 'Accept' }}</button>
                                    <button type="button" class="fdd-x" title="Not now: drop it from today's digest" :disabled="busy || x.saving" @click="skip(x)">×</button>
                                </div>
                            </div>
                            <div v-if="digest.moreUndated" class="fdd-hint">{{ digest.moreUndated }} more without a date. Tidy my board on each deck suggests dates for all of them.</div>
                        </div>

                        <!-- Signatures still needed from this person (API pendingSignatures: visible nodes without a current sign-off) -->
                        <div v-if="signCount" class="fdd-sec fdd-sign">
                            <div class="fdd-label">Signatures needed · {{ signCount }}</div>
                            <a v-for="n in signatures.items" :key="n.slug" href="#" class="fdd-row" @click.prevent="openNode(n)">
                                <span class="fdd-when">{{ n.typeLabel }}</span>
                                <span class="fdd-main"><b>{{ n.name }}</b><span>{{ n.signedBefore ? 'Changed since you signed it' : 'Not signed yet' }}<template v-if="n.lastModified"> · updated {{ shortDate(String(n.lastModified).slice(0, 10)) }}</template></span></span>
                            </a>
                            <div v-if="signCount > signatures.items.length" class="fdd-hint">{{ signCount - signatures.items.length }} more on your Home page.</div>
                        </div>

                        <p v-if="digest.notes" class="fdd-hint">{{ digest.notes }}</p>
                        <p v-if="!meetings.length && !digest.urgent.length && !digest.noDueDate.length && !signCount && !handoffCount && !rocks.items.length && !rocks.milestones.length" class="fdd-hint">Nothing needs you right now.</p>
                        <div class="fdd-foot">
                            <span class="fdd-hint">Made {{ madeAt }}. Suggestions only: nothing changes until you accept it.</span>
                            <button type="button" class="ai-btn ai-btn-ghost fdd-small" :disabled="busy || refreshing" @click="refresh()">{{ refreshing ? 'Refreshing…' : 'Refresh' }}</button>
                        </div>
                        <div v-if="notice" class="fdd-hint" role="status">{{ notice }}</div>
                    </template>
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { browserTimeZone } from '@/meetingTime';

export default defineComponent({
    name: 'ForgeDailyDigest',
    // inMeetings: on Meetings Home, meetings and to-dos open in place (open-meeting / open-item, as MeetingsHome's own rows)
    // buttonMode: no bar on the page; the host opens the popup through openPop() (the deck board's "The Daily Digest" button)
    // boardActions: the popup shows the Forge's deck actions (Tidy / Plan my week / Sort the Inbox) and emits `forge` with the kind
    props: { inMeetings: { type: Boolean, default: false }, buttonMode: { type: Boolean, default: false }, boardActions: { type: Boolean, default: false } },
    emits: [ 'open-meeting', 'open-item', 'forge' ],
    data() {
        return {
            open: false,
            loading: true, refreshing: false, busy: false,
            hidden: false, error: '', notice: '',
            digest: null as any
        };
    },
    computed: {
        counts(): any { return (this.digest && this.digest.counts) || {}; },
        meetings(): any[] { return (this.digest && this.digest.meetings) || []; },
        acceptable(): any[] { return ((this.digest && this.digest.noDueDate) || []).filter((x: any) => x.pick && !x.saving); },
        signatures(): any { return (this.digest && this.digest.signatures) || { count: 0, items: [] }; },
        signCount(): number { return Number(this.signatures.count) || 0; },
        handoffs(): any { return (this.digest && this.digest.handoffs) || { count: 0, items: [] }; },
        handoffCount(): number { return Number(this.handoffs.count) || 0; },
        handoffChip(): string { return `${this.handoffCount} recording${this.handoffCount == 1 ? '' : 's'} to build`; },
        rocks(): any { return (this.digest && this.digest.rocks) || { quarter: {}, items: [], milestones: [], counts: {} }; },
        rockCounts(): any { return this.rocks.counts || {}; },
        // One milestone chip, the most pressing state first: late, due today, due this week
        milestoneChip(): { text: string, cls: string } | null {
            const c = this.rockCounts;
            if(c.late) return { text: `${c.late} milestone${c.late == 1 ? '' : 's'} late`, cls: 'fdd-chip-hot' };
            if(c.dueToday) return { text: `${c.dueToday} milestone${c.dueToday == 1 ? '' : 's'} due today`, cls: 'fdd-chip-warn' };
            if(c.dueThisWeek) return { text: `${c.dueThisWeek} milestone${c.dueThisWeek == 1 ? '' : 's'} this week`, cls: '' };
            return null;
        },
        myRocksLink(): string { return `/scoreboard?view=rocks&quarter=${this.rocks.quarter.key || ''}&mine=1`; },
        rockChip(): string {
            const c = this.rockCounts;
            return `${c.rocks} rock${c.rocks == 1 ? '' : 's'}${c.off ? ` · ${c.off} off track` : ''}`;
        },
        // The bar's "next meeting" state: the first meeting today (with its time), else the first tomorrow
        nextMeeting(): string {
            const m = this.meetings.find((x: any) => x.when == 'today') || this.meetings[0];
            if(!m) return '';
            return `${m.when == 'today' ? 'Next' : 'Tomorrow'}: ${m.name}${m.when == 'today' && m.time ? ' ' + this.timeLabel(m.time) : ''}`;
        },
        madeAt(): string {
            const t = this.digest && this.digest.createdAt;
            return t ? new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'today';
        }
    },
    async created() { await this.load(); },
    beforeUnmount() { this.closePop(); },
    methods: {
        openPop() {
            this.open = true;
            document.body.classList.add('fdd-no-scroll');
            this.$nextTick(() => { const el = this.$refs.pop as HTMLElement | undefined; if(el) el.focus(); });
        },
        closePop() {
            this.open = false;
            document.body.classList.remove('fdd-no-scroll');
        },
        show(d: any) {
            if(!d || d.noEmployee) { this.hidden = true; return; }
            d.urgent = d.urgent || [];
            d.noDueDate = (d.noDueDate || []).map((x: any) => ({ ...x, pick: x.suggestedDue || '', saving: false, error: '' }));
            d.rocks = this.rocksState(d.rocks);
            this.digest = d;
        },
        rocksState(r: any) {
            if(!r) return { quarter: {}, items: [], milestones: [], counts: {} };
            return { ...r, milestones: (r.milestones || []).map((m: any) => ({ ...m, saving: false, error: '' })) };
        },
        // "Pincher Creek · 2/5 milestones · 1 late · next: Pour the slab, Oct 24"
        rockLine(r: any): string {
            const parts = [];
            if(r.team) parts.push(r.team + (r.teamRock ? ' team rock' : ''));
            parts.push(r.total ? `${r.done}/${r.total} milestones` : 'No milestones yet');
            if(r.late) parts.push(`${r.late} late`);
            if(r.next) parts.push(`next: ${r.next.text}, ${r.next.bucket == 'today' ? 'today' : this.shortDate(r.next.due)}`);
            return parts.join(' · ');
        },
        openLink(link: string) {
            this.closePop();
            this.$router.push(link);
        },
        // Checks the milestone off through the Rocks page's route (its owner, or anyone who can update the rock), then
        // re-reads the digest: the rocks section is live, so the milestone and its chip drop out
        async milestoneDone(m: any) {
            if(m.saving) return;
            m.saving = true; m.error = '';
            try {
                await axios.post(`${this.$store.state.apiUrl}/api/rocks/milestone`, { ID: m.accept.milestoneID, Done: true });
                const d = (await axios.get(`${this.$store.state.apiUrl}/api/ai/daily-digest`, { params: { tz: browserTimeZone() } })).data;
                this.digest.rocks = this.rocksState(d.rocks);
                this.digest.urgent = this.digest.urgent.filter((u: any) => u.ref != m.ref);
                this.notice = `${m.name}: done.`;
            } catch(e: any) {
                m.error = this.errorText(e, 'That milestone didn\'t save.');
                m.saving = false;
            }
        },
        errorText(e: any, fallback: string): string {
            const d = e?.response?.data, s = e?.response?.status;
            if(s == 403) return 'You don\'t have permission to change that.';
            if(s == 404) return 'That isn\'t there any more, or you can\'t edit it.';
            return typeof d == 'string' && d && d.length < 300 ? d : fallback;
        },
        async load() {
            this.loading = true; this.error = '';
            try {
                this.show((await axios.get(`${this.$store.state.apiUrl}/api/ai/daily-digest`, { params: { tz: browserTimeZone() } })).data);
            } catch(e: any) {
                // Logins the AI module isn't open to just don't get the card
                if(e?.response?.status == 403) this.hidden = true;
                else this.error = this.errorText(e, 'The Forge couldn\'t put your digest together just now.');
            }
            this.loading = false;
        },
        async refresh() {
            this.refreshing = true; this.notice = '';
            try {
                this.show((await axios.post(`${this.$store.state.apiUrl}/api/ai/daily-digest/refresh`, { tz: browserTimeZone() })).data);
            } catch(e: any) {
                this.notice = this.errorText(e, 'Couldn\'t refresh just now. Please try again.');
            }
            this.refreshing = false;
        },
        shortDate(d: string): string {
            return d ? new Date(d + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
        },
        timeLabel(hm: string): string {
            const [ h, m ] = hm.split(':').map(n => parseInt(n));
            return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
        },
        forge(kind: string) {
            this.closePop();
            this.$emit('forge', kind);
        },
        openNode(n: any) {
            this.closePop();
            this.$router.push(n.link);
        },
        openMeeting(m: any) {
            this.closePop();
            if(this.inMeetings) this.$emit('open-meeting', { ID: m.meetingID, Team: m.team });
            else this.$router.push(m.link);
        },
        openItem(x: any) {
            this.closePop();
            if(this.inMeetings && x.kind == 'todo') this.$emit('open-item', { ID: x.accept.itemID, MeetingID: x.accept.meetingID, Team: x.accept.team || null });
            else this.$router.push(x.link);
        },
        parse(v: any, fallback: any) {
            if(v && typeof v == 'object') return v;
            try { return JSON.parse(v || ''); } catch(e) { return fallback; }
        },
        // Sets the date through the item's own route, so its permission checks decide
        async setDue(x: any, date: string) {
            const api = this.$store.state.apiUrl, a = x.accept;
            if(a.kind == 'todo') {
                const items = (await axios.get(`${api}/api/meetingitems/${a.meetingID}`)).data || [];
                const it = items.find((i: any) => Number(i.ID) == a.itemID);
                if(!it) throw { response: { status: 404 } };
                await axios.post(`${api}/api/meetingitems`, { ID: it.ID, MeetingID: it.MeetingID, Name: it.Name, Description: it.Description, Priority: it.Priority,
                    AssigneeEmployeeID: it.AssigneeEmployeeID, Private: it.Private, DueDate: date });
                return;
            }
            const cards = (await axios.get(`${api}/api/companyboarditems/${encodeURIComponent(a.slug)}`)).data || [];
            const c = cards.find((k: any) => Number(k.ID) == a.cardID);
            if(!c || c.MeetingItem) throw { response: { status: 404 } };
            if(a.kind == 'card') {
                await axios.post(`${api}/api/companyboarditems`, { ...c, TagsJSON: this.parse(c.TagsJSON, []), Date: date, changedField: 'due date' });
                return;
            }
            // A checklist step: found by position, or by name if the list was reordered since the digest was made
            const lists = this.parse(c.ChecklistJSON, []);
            let list = lists[a.listIndex], item = list && (list.items || [])[a.itemIndex];
            if(!item || item.name != a.itemName) {
                list = lists.find((l: any) => l.name == a.listName) || list;
                item = list && (list.items || []).find((i: any) => i.name == a.itemName && !i.checked);
            }
            if(!item) throw { response: { status: 404 } };
            item.date = date;
            await axios.post(`${api}/api/companyboarditems/checklist`, { id: c.ID, slug: a.slug, checklist: lists.map((l: any) => ({ name: l.name, items: l.items || [] })), updatedChecklist: list.name });
        },
        async handled(x: any) {
            await axios.post(`${this.$store.state.apiUrl}/api/ai/daily-digest/handled`, { ref: x.ref, tz: browserTimeZone() }).catch(() => null);
            this.digest.noDueDate = this.digest.noDueDate.filter((y: any) => y.ref != x.ref);
            this.digest.urgent = this.digest.urgent.filter((y: any) => y.ref != x.ref);
        },
        async accept(x: any): Promise<boolean> {
            if(!x.pick || x.saving) return false;
            x.saving = true; x.error = '';
            try {
                await this.setDue(x, x.pick);
                if(this.counts.noDueDate) this.counts.noDueDate--;
                await this.handled(x);
                return true;
            } catch(e: any) {
                x.error = this.errorText(e, 'That date didn\'t save.');
                return false;
            } finally {
                x.saving = false;
            }
        },
        async acceptAll() {
            this.busy = true;
            let n = 0;
            for(const x of [ ...this.acceptable ]) if(await this.accept(x)) n++;
            this.notice = `Set ${n} due date${n == 1 ? '' : 's'}.`;
            this.busy = false;
        },
        async skip(x: any) {
            await this.handled(x);
        }
    }
});
</script>

<style scoped>
.fdd { margin-bottom: 16px; }
.fdd-button-mode { margin: 0; }
.fdd-bar { display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px 14px; border: 1px solid var(--ai-border-soft); border-radius: 14px; background: var(--ai-grad-soft); box-shadow: var(--ai-shadow-card); font-family: inherit; text-align: left; cursor: pointer; color: inherit; }
.fdd-bar:hover, .fdd-bar:focus-visible { border-color: var(--ai-accent-deep); outline: none; }
.fdd-icon { width: 30px; height: 30px; border-radius: 9px; background: var(--ai-grad); color: #fff; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.fdd-icon svg { width: 16px; height: 16px; }
.fdd-titles { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.fdd-title { font-family: var(--head-font); font-weight: 700; font-size: 15px; color: var(--dark-gray); }
.fdd-headline { font-size: 13px; color: var(--font-dark-gray); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fdd-states { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px; flex-shrink: 0; max-width: 55%; }
.fdd-states span, .fdd-chips span { font-size: 12px; background: #fff; border: 1px solid var(--ai-border-soft); border-radius: 10px; padding: 2px 9px; color: var(--font-dark-gray); white-space: nowrap; }
.fdd-chips span { background: var(--light-gray); border: none; }
.fdd-chip-hot { background: #fde2e1 !important; border-color: #f5c2c0 !important; color: #a1262a !important; font-weight: 700; }
.fdd-chip-warn { background: #fff1e0 !important; border-color: #f7d9b5 !important; color: var(--ai-accent-deep) !important; font-weight: 700; }
.fdd-chip-sign { background: #e8f1fb !important; border-color: #c5dbf2 !important; color: #1b4f8a !important; font-weight: 700; }
.fdd-chip-rec { background: #efe9fb !important; border-color: #d6c9f2 !important; color: #4f2d8f !important; font-weight: 700; }
.fdd-chip-next { max-width: 260px; overflow: hidden; text-overflow: ellipsis; }
.fdd-label-soft { font-weight: 400; color: var(--font-mid-gray); }
.fdd-sublabel { font-size: 12px; font-weight: 700; color: var(--font-dark-gray); padding: 10px 0 2px; }
.fdd-pill { display: inline-block; font-size: 11px; font-weight: 700; border-radius: 8px; padding: 2px 7px; line-height: 1.3; }
.fdd-pill-ok { background: #e3f3e8; color: #2E7D46; }
.fdd-pill-bad { background: #fde2e1; color: #a1262a; }
.fdd-deck-acts { display: flex; flex-wrap: wrap; gap: 8px; padding-top: 8px; }
.fdd-deck-acts .ai-btn { flex-direction: column; align-items: flex-start; gap: 1px; padding: 7px 12px; }
.fdd-deck-acts .ai-btn b { font-size: 13px; }
.fdd-deck-acts .ai-btn span { font-size: 11.5px; font-weight: 400; opacity: .8; }
.fdd-open-label { font-family: var(--head-font); font-weight: 700; font-size: 12.5px; color: var(--ai-accent-deep); flex-shrink: 0; }
.fdd-wrap { z-index: 9999999; }
.fdd-pop { width: 760px; padding: 20px 24px 22px; outline: none; }
.fdd-close { position: absolute; top: 8px; right: 12px; border: none; background: none; font-size: 26px; line-height: 1; cursor: pointer; color: var(--font-mid-gray); }
.fdd-close:hover { color: var(--dark-gray); }
.fdd-pop-h { display: flex; align-items: center; gap: 12px; padding-right: 30px; }
.fdd-pop-h .fdd-headline { white-space: normal; }
.fdd-body { padding-top: 4px; }
.fdd-summary { font-size: 13.5px; color: var(--dark-gray); margin: 8px 0; line-height: 1.45; }
.fdd-chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 4px 0 6px; }
.fdd-sec { margin-top: 12px; }
.fdd-label { font-size: 12px; font-weight: 700; color: var(--font-dark-gray); padding-bottom: 4px; border-bottom: 1px solid var(--mid-gray); }
.fdd-label-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.fdd-row { display: flex; gap: 12px; align-items: flex-start; padding: 8px 0; border-bottom: 1px solid var(--light-gray); text-decoration: none; color: inherit; }
.fdd-row:hover b { color: var(--ai-accent-deep); }
.fdd-when { width: 96px; flex-shrink: 0; font-family: var(--head-font); font-weight: 700; font-size: 12.5px; color: var(--dark-gray); }
.fdd-hot { color: #a1262a; }
.fdd-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.fdd-main b { font-size: 13.5px; color: var(--dark-gray); overflow-wrap: anywhere; }
.fdd-main span { font-size: 12px; color: var(--font-dark-gray); }
.fdd-main a { text-decoration: none; color: inherit; }
.fdd-due { display: flex; gap: 10px; align-items: center; padding: 8px 0; border-bottom: 1px solid var(--light-gray); flex-wrap: wrap; }
.fdd-due-act { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.fdd-due-act input { width: 140px; margin: 0; padding: 4px 6px; font-size: 13px; }
.fdd-small { padding: 5px 12px; font-size: 12.5px; }
.fdd-x { border: none; background: none; font-size: 18px; line-height: 1; color: var(--font-mid-gray); cursor: pointer; padding: 2px 6px; }
.fdd-x:hover { color: var(--dark-gray); }
.fdd-item-error { color: #a1262a !important; font-weight: 600; }
.fdd-hint { font-size: 12px; color: var(--font-mid-gray); margin: 8px 0 0; }
.fdd-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-top: 10px; }
.fdd-foot .fdd-hint { margin: 0; }
.fdd-error { font-size: 13px; color: #a1262a; margin: 10px 0; }
.fdd-loading { display: flex; align-items: center; gap: 10px; font-size: 13px; color: var(--font-dark-gray); padding: 12px 0; }
.fdd-orb { width: 16px; height: 16px; border-radius: 50%; background: var(--ai-grad); animation: fdd-pulse 1.2s ease-in-out infinite; }
@keyframes fdd-pulse { 0%, 100% { opacity: .4; transform: scale(.85); } 50% { opacity: 1; transform: scale(1); } }
@media (max-width: 900px) {
    .fdd-chip-next { display: none; }
}
@media (max-width: 600px) {
    .fdd-bar { flex-wrap: wrap; }
    .fdd-titles { flex-basis: calc(100% - 42px - 44px); }
    .fdd-states { max-width: 100%; justify-content: flex-start; width: 100%; order: 3; }
    .fdd-pop { padding: 18px 14px; }
    .fdd-when { width: 72px; }
    .fdd-due-act { width: 100%; }
    .fdd-due-act input { flex: 1; width: auto; }
}
</style>
<style>
/* Unscoped on purpose: the body class that stops the page scrolling behind the popup */
body.fdd-no-scroll { overflow: hidden; }
</style>
